import React, { useState, useEffect } from 'react';
import '../styles/Banner.css';
import bannerImg from '../assets/banner.png';
import shapeCircle from '../assets/circle.png';
import shapeTriangle from '../assets/triangle.png';
import shapeMoon from '../assets/moon.png';
import api from '../api';
import { useNavigate } from 'react-router';
import { useTr } from '../lib/i18n';
import { listPathLang, CITY_RU } from '../lib/ru';

const PROPERTY_RU = { 1: 'Квартиры', 2: 'Дома', 3: 'Коммерческая недвижимость', 4: 'Участки', 10: 'Гаражи', 11: 'Прочее' };
const PROPERTY_BASE = { 1: 'apartamente', 2: 'case', 3: 'spatii-comerciale', 4: 'terenuri', 10: 'garajuri', 11: 'altele' };

const Banner = () => {
    const tr = useTr();
    const lang = tr('ro', 'ru');
    const [locations, setLocations] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const [saleType, setSaleType] = useState(1); // 1 = Vânzare, 2 = Chirie
    const [location, setLocation] = useState('');
    const [sector, setSector] = useState('');
    const [propertyType, setPropertyType] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [locationsRes, sectorsRes, typesRes] = await Promise.all([
                    api.get('/api/v1/locations/'),
                    api.get('/api/v1/sectors/'),
                    api.get('/api/v1/property-types/'),
                ]);
                setLocations(locationsRes.data);
                setSectors(sectorsRes.data);
                setPropertyTypes(typesRes.data);
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError(tr('Nu s-au putut încărca datele. Încercați din nou mai târziu.', 'Не удалось загрузить данные. Попробуйте позже.'));
                setIsLoading(false);
                navigate('/404');
            }
        };
        fetchData();
    }, [navigate]);

    const locationName = (id) => {
        const loc = locations.find((l) => l.id === Number(id));
        if (!loc) return '';
        if (lang === 'ru') return CITY_RU[loc.slug]?.name || loc.location;
        return loc.location;
    };

    const handleSearch = () => {
        if (propertyType && location) {
            const locSlug = locations.find(l => l.id === Number(location))?.slug ?? location;
            const secSlug = sector ? (sectors.find(s => s.id === Number(sector))?.slug ?? sector) : '';
            navigate(listPathLang(lang, PROPERTY_BASE[propertyType] || propertyType, { location: locSlug, sector: secSlug, saleType }));
        } else {
            alert(tr('Vă rugăm să selectați o locație și un tip de proprietate.', 'Пожалуйста, выберите локацию и тип недвижимости.'));
        }
    };

    if (isLoading) {
        return <div className="text-center p-4">{tr('Se încarcă...', 'Загрузка...')}</div>;
    }

    if (error) {
        return <div className="text-center p-4 text-red-500">{error}</div>;
    }

    return (
        <div className="relative flex flex-col xl:flex-row justify-between w-full">
            <div className="xl:w-1/2 order-1 text-center xl:text-left">
                <h5 className="text-[14px] font-body tracking-wider font-bold uppercase mb-4">
                    LVG Imob
                </h5>
                <h1 className="text-[24px] xs:text-[38px] sm:text-[48px] md:text-[62px] text-gray-800 font-bold font-poppins leading-snug text-black mb-4">
                    {tr('LVG Imob – Partenerul Tău de Încredere', 'LVG Imob — ваш надёжный партнёр')}{' '}
                    <span className="whitespace-nowrap">
                        {tr('în', 'в')} <span className="text-[#d5ab63]">{tr('Imobiliare', 'Недвижимости')}</span>
                    </span>
                </h1>
                <p className="text-[#181616] font-body max-w-[450px] mx-auto xl:mx-0">
                    {tr(
                        'Vinde sau cumpără proprietăți în Moldova, fără complicații. La LVG Imob, îți oferim o experiență imobiliară completă – fie că vinzi, cumperi sau doar cauți consultanță de specialitate.',
                        'Продавайте или покупайте недвижимость в Молдове без лишних сложностей. LVG Imob предлагает полное сопровождение — продажа, покупка или профессиональная консультация.'
                    )}
                </p>
            </div>

            <div className="xl:w-1/2 order-3 relative mt-12 xl:mt-0 flex justify-center xl:justify-end">
                <img src={bannerImg} alt="Grid building" className="w-full" fetchPriority="high" decoding="async" />
                <img
                    src={shapeCircle}
                    className="shape shape-circle absolute w-[60px] top-[25%] right-[59%] lg:w-[80px] lg:top-[23%] lg:right-[48%]"
                    alt="circle" decoding="async"
                />
                <img
                    src={shapeTriangle}
                    className="shape shape-triangle absolute w-[40px] top-[15%] right-[87%] lg:w-[60px] lg:top-[15%] lg:right-[75%]"
                    alt="triangle" decoding="async"
                />
                <img
                    src={shapeMoon}
                    className="shape shape-moon absolute w-[60px] top-[30%] right-[12%] lg:w-[80px] lg:top-[29%] lg:right-[22%] xl:right-[1%]"
                    alt="moon" decoding="async"
                />
            </div>

            <div className="flex order-2 flex-col my-[50px] lg:my-[0px] gap-[20px] xl:absolute top-[500px] 2xl:top-[440px]">
                <div className="flex gap-[20px]">
                    <button
                        onClick={() => setSaleType(2)}
                        className={`${
                            saleType === 2
                                ? 'btn-main text-white'
                                : 'bg-white text-[#181616] hover:bg-gray-200 transition-all duration-100 ease-in border-[1px] border-gray-300'
                        } px-[30px] py-[12px] rounded-[5px] cursor-pointer`}
                    >
                        {tr('Chirie', 'Аренда')}
                    </button>
                    <button
                        onClick={() => setSaleType(1)}
                        className={`${
                            saleType === 1
                                ? 'btn-main text-white'
                                : 'bg-white text-[#181616] hover:bg-gray-200 transition-all duration-100 ease-in border-[1px] border-gray-300'
                        } px-[30px] py-[12px] rounded-[5px] cursor-pointer`}
                    >
                        {tr('Vânzare', 'Продажа')}
                    </button>
                </div>
                <div className="bg-white shadow-lg flex flex-col flex-wrap lg:flex-row gap-[30px] z-[49] justify-between lg:gap-10 p-[40px] rounded-[5px]">
                    <select
                        name="location"
                        className="font-body px-[30px] py-[12px] text-[#181616] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px] min-w-[220px]"
                        value={location}
                        onChange={(e) => {
                            setLocation(e.target.value);
                            setSector(''); // Resetează sectorul când schimbi locația
                        }}
                    >
                        <option value="" disabled>
                            {tr('Locație', 'Локация')}
                        </option>
                        {locations.length > 0 ? (
                            locations.map((loc) => (
                                <option key={loc.id} value={loc.id}>
                                    {locationName(loc.id)}
                                </option>
                            ))
                        ) : (
                            <option disabled>{tr('Nu sunt disponibile locații', 'Нет доступных локаций')}</option>
                        )}
                    </select>

                    {location === '1' && (
                        <select
                            name="sector"
                            className="font-body px-[30px] py-[12px] text-[#181616] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px] min-w-[220px]"
                            value={sector}
                            onChange={(e) => setSector(e.target.value)}
                        >
                            <option value="" disabled>
                                {tr('Sector', 'Сектор')}
                            </option>
                            {sectors.length > 0 ? (
                                sectors.map((sector) => (
                                    <option key={sector.id} value={sector.id}>
                                        {sector.sector}
                                    </option>
                                ))
                            ) : (
                                <option disabled>{tr('Nu sunt disponibile sectoare', 'Нет доступных секторов')}</option>
                            )}
                        </select>
                    )}

                    <select
                        name="propertyType"
                        className="font-body px-[30px] py-[12px] text-[#181616] rounded-[5px] outline-none border-[1px] border-gray-300 text-[17px] min-w-[220px]"
                        value={propertyType}
                        onChange={(e) => setPropertyType(e.target.value)}
                    >
                        <option value="" disabled>
                            {tr('Tipul proprietății', 'Тип недвижимости')}
                        </option>
                        {propertyTypes.length > 0 ? (
                            propertyTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                    {lang === 'ru' ? (PROPERTY_RU[type.id] || type.type) : type.type}
                                </option>
                            ))
                        ) : (
                            <option disabled>{tr('Nu sunt disponibile tipuri de proprietăți', 'Нет доступных типов недвижимости')}</option>
                        )}
                    </select>

                    <button
                        onClick={handleSearch}
                        className="btn-main text-white px-[45px] py-[12px] rounded-[5px] cursor-pointer"
                    >
                        {tr('Caută', 'Поиск')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Banner;
